import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { ScrollTrigger } from "gsap/ScrollTrigger";
import cpgSquare from './../../assets/images/cpg-shapes-square.svg';
import cpgTriangle from './../../assets/images/cpg-shapes-triangle.svg';
import '../cpg.scss';
import { gsap } from 'gsap';
import AOS from "aos";
import "aos/dist/aos.css";

export default function CPGMetrics() {

  const square1Ref = useRef();
  const square2Ref = useRef();
  const triangle1Ref = useRef();
  const triangle2Ref = useRef();

  gsap.registerPlugin(ScrollTrigger);

  useEffect(() => {

    document.addEventListener('aos:in:bar-anim', function(ev) {
      //console.log('animated in', ev);

      if (ev.detail.querySelectorAll('.progress').length > 1) {
        //console.log("more than 1 progress");
        var barArray = [...ev.detail.querySelectorAll('.progress')];
      } else {
        //console.log("less than 1 progress");
        var barArray = [];
        var bar = ev.detail.querySelector('.progress');
        //console.log(bar);
        barArray.push(bar);
      }

      //console.log(barArray);

      if (barArray && barArray.length) {
        barArray.forEach(function(barItem) {
          var fill = barItem.getAttribute('data-fill');

          if (!ev.detail.classList.contains('vertical-bar-chart')) {
            barItem.style.width = fill + '%';
          }
        });
      }

      var Cont={val:0};

      if (ev.detail.querySelectorAll('.percentage').length > 1) {
        var percentageArray = [...ev.detail.querySelectorAll('.percentage')];
        //console.log("more than 1 percentage");
      } else {
        var percentageArray = [];
        var percentage = ev.detail.children[0].querySelector('.percentage');
        percentageArray.push(percentage);
        //console.log("less than percentage");

      }

      //console.log(percentage);

      if (percentageArray) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }

      //console.log(percentageArray);

    });

    document.addEventListener('aos:in:percentage', function(ev) {
      var Cont={val:0};
      var percentage = ev.detail.children[0].querySelector('.percentage');
      var percentageArray = [];
      percentageArray.push(percentage);
      //console.log(percentage);

      if (percentage) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }
    });

    // Had to make some adjustments since it seemed like the animation wasn't playing nice with React. The shapes weren't incrementing exactly 90 degrees (it was adding a few degrees with decimal places) so they always rotated crooked. Possibly React strict mode causing issues. Added gsap.context, refs, and using GSAP's ScrollTrigger instead of AOS. 
    let ctx = gsap.context(() => {

      var tl = gsap.timeline({ 
        onComplete:rotateAgain,
        scrollTrigger: {
          trigger: ".shape-images"
        }
      });

      function animate(){
        tl
        .to(triangle1Ref.current, 0.5, {rotation:"+=90"})
        .to(square1Ref.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(triangle2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(square2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
      }

      function rotateAgain(){
        gsap.delayedCall(5, animate)
      }

      animate();

    });

    return () => ctx.revert(); // <- cleanup!

    AOS.init({
      once: true
    });
    AOS.refresh();
  }, []);

  return (
    <>
      <div className="cpg-metrics">

        <p id="s1" className="intro" data-aos="fade-up">See how businesses in the consumer packaged goods (CPG) industry track and measure direct mail success.&#8288;<a href="#reference-1" className="cm-article-content__reference" aria-label="Footnote 1" title="Footnote 1"><sup aria-hidden="true">[1]</sup></a></p>
        <div className='two-cols'>
          <div className='col-right'>
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Checking Mail Effectiveness</h3>
                <div className='pie-chart' data-aos>
                  <svg viewBox="0 0 32 32" role="img" alt="" title="">
                    <circle r="16" cx="16" cy="16" strokeDasharray="0 100"></circle>
                  </svg>
                </div>

                <span data-aos data-aos-id="percentage" data-aos-anchor=".pie-chart">
                  <span data-aos className='percentage-value'><span class="percentage" data-to="91" aria-hidden="true">0</span><span class="screen-reader-text">91</span>%</span>
                </span>

                <p>Always or most times</p>
                <p>Ninety-one percent of CPG companies reported checking the effectiveness of their direct mail campaigns always or most of the time.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>Measuring direct mail effectiveness is crucial for shaping future campaigns. By better understanding what kind of messaging, design, offers and formats work best, brands can create more effective and actionable marketing.</p>
              </div>
            </div> 
          </div>

          <div className='col-left'>
            <div className='inner-cols'>
              <div className='inner-col-item'>
                <div className='rounded-box-module-wrap'>
                  <div className="rounded-box-module" data-aos="fade-up">
                    <h3>Direct Mail Response Rates</h3>
                    <div className='progress-bar-area' data-aos data-aos-id="bar-anim">
                      <div className='progressbar-item'>
                        <span className='label'>Less than 5%</span>
                        <div className='progress-bar size-small'>
                          <div className='progress fill-light' data-fill="9"><span class="percentage" data-to="9" aria-hidden="true">0</span><span class="screen-reader-text">9</span>%</div>
                        </div>
                      </div>
                      <div className='progressbar-item'>
                        <span className='label'>From 6% to 10%</span>
                        <div className='progress-bar size-small'>
                          <div className='progress fill-light' data-fill="9"><span class="percentage" data-to="9" aria-hidden="true">0</span><span class="screen-reader-text">9</span>%</div>
                        </div>
                      </div>
                      <div className='progressbar-item'>
                        <span className='label'>From 11% to 15%</span>
                        <div className='progress-bar size-small'>
                          <div className='progress fill-light' data-fill="23"><span class="percentage" data-to="23" aria-hidden="true">0</span><span class="screen-reader-text">23</span>%</div>
                        </div>
                      </div>
                      <div className='progressbar-item'>
                        <span className='label'>From 16% to 20%</span>
                        <div className='progress-bar size-small'>
                          <div className='progress fill-light' data-fill="26"><span class="percentage" data-to="26" aria-hidden="true">0</span><span class="screen-reader-text">26</span>%</div>
                        </div>
                      </div>
                      <div className='progressbar-item'>
                        <span className='label'>From 21% to 25%</span>
                        <div className='progress-bar size-small'>
                          <div className='progress' data-fill="29"><span class="percentage" data-to="29" aria-hidden="true">0</span><span class="screen-reader-text">29</span>%</div>
                        </div>
                      </div>
                      <div className='progressbar-item'>
                        <span className='label'>More than 25%</span>
                        <div className='progress-bar size-small'>
                          <div className='progress fill-light' data-fill="6"><span class="percentage" data-to="6" aria-hidden="true">0</span><span class="screen-reader-text">6</span>%</div>
                        </div>
                      </div>
                    </div>
                    <p>CPG businesses report high direct mail response rates, with most saying mail resulted in response rates of 21%–25%.</p>
                  </div>
                  <div className="rounded-box-module">
                    <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                    <p id="s2">Personalizing mail can make customers feel special and increase the chances of engagement. Look at the data: Do some customers buy certain products on a regular basis? Are customers increasingly responding to messaging highlighting your eco-friendly practices? Figure out how you can address segments’ varying wants, needs and values, then customize your marketing accordingly.</p>
                  </div>
                </div>
              </div>
              <div className='inner-col-item'>
                <div className='rounded-box-module-wrap'>
                  <div className="rounded-box-module" data-aos="fade-up">
                    <h3>Mail ROI and Cost Effectiveness</h3>
                    <div className='progress-bar' data-aos data-aos-id="bar-anim">
                      <div className='progress' data-fill="51"><span class="percentage" data-to="51" aria-hidden="true">0</span><span class="screen-reader-text">51</span>%</div>
                    </div>
                    <p> CPG</p>
                    <p className="no-margin-bottom"><strong>32%</strong> — Retail </p>
                    <p className="no-margin-bottom"><strong>28%</strong> — Insurance</p>
                    <p className="no-margin-bottom"><strong>36%</strong> — Health</p>
                    <p><strong>30%</strong> — Financial</p>
                    <p>Out of all industries, CPG businesses are the most likely to say direct mail is cost-effective and results in a good ROI.</p>
                  </div>
                  <div className="rounded-box-module">
                    <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                    <p>Connecting mail to digital marketing efforts makes it easier to track direct mail ROI. <a href="/../track-direct-mail-roi/">Use vanity URLs, match-backs, retargeting and more</a> to get started.</p>
                  </div>
                </div>
              </div>
            </div>


          </div>
        </div>
        <div className='two-cols'>
          <div className='col-left w-50'>
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Measuring Mail Impact</h3>
                <div className='progress-bar' data-aos data-aos-id="bar-anim">
                  <div className='progress' data-fill="75"><span class="percentage" data-to="75" aria-hidden="true">0</span><span class="screen-reader-text">75</span>%</div>
                </div>
                <p>Customer satisfaction</p>
                <p>CPG companies typically measure the impact of direct mail by tracking customer satisfaction. They’re also more likely than any other industry to do so.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>Take a holistic approach to measuring customer satisfaction, assessing customer reviews, customer service complaints and repeat business rates to get an idea of what’s working and what’s not. You can also send out surveys to get customer input directly—perhaps with an incentive, such as a small discount or freebie.</p>
              </div>
            </div>
          </div>
          <div className='col-left w-50'>
            <div className='personalized-box secondary align-to-text'>
              <h3>CPG companies usually measure mail impact through customer satisfaction.</h3>
              <div data-aos data-aos-id="vector-shapes" className='shape-images'>
                <div className='triangle triangle-1'>
                  <img ref={triangle1Ref} src={cpgTriangle} alt="" />
                </div>
                <div className='square square-1'>
                  <img ref={square1Ref} src={cpgSquare} alt="" />
                </div>
                <div className='triangle triangle-2'>
                  <img ref={triangle2Ref} src={cpgTriangle} alt="" />
                </div>
                <div className='square square-2'>
                  <img ref={square2Ref} src={cpgSquare} alt="" />
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}
